// Generic imports
import _ from 'lodash';
import pluralize from 'pluralize';

// Custom imports
import { splitProductsByType, getProductName } from './dashboard/utils/product.js';
import { getFieldRatesForProduct } from './dashboard/utils/rates.js';
import { getMultiplier, calcWeight } from './CalcWeight.js';
import { dangerousText } from './utils/Dangerous.js';

export const FieldTypes = {
    air: [{
        label: 'Per Product – Chargeable Weight',
        value: 'per-unit-type',
        getChargeLines: ({ products, buyingRates, fieldBuyingRates, organization }) => {
            return products.map((product, i) => {
                const { multiplier, multiplierText } = getMultiplier({ product, transportationMethod: 'air', allProducts: products, round: organization.airRoundChargeableWeight });
                // allProducts is only [product] because we want to calculate the rates for the product only when chargeable weigh
                const rates = getFieldRatesForProduct({ fieldBuyingRates, product, buyingRates, transportationMethod: 'air', allProducts: [product], organization });
                return {
                    fieldValueId: product?.id,
                    productsInChargeLine: [product],
                    title: getProductName({ product }),
                    description: product.dangerous ? dangerousText(product) : null,
                    quantityMultiplier: multiplier,
                    multiplierText: `${multiplierText} (cw)`,
                    multiplierTextSingular: `${multiplierText} (cw)`,
                    buyingRates: rates,
                }
            });
        },
        // Air freight is now a single unified product; per-product options are equivalent to the
        // aggregated "Priced by ..." options, so hide them everywhere. Kept in the array so existing
        // offers referencing 'per-unit-type' continue to work.
        enabled: () => false,
    }, {
        label: 'Per Product – Gross Weight',
        value: 'per-unit-type-weight',
        enabled: () => false,
        getChargeLines: ({ products, buyingRates, fieldBuyingRates, organization }) => {
            return products.map((product, i) => {
                const multiplier = product.weight * (product.quantity || 1);
                const multiplierText = product.kgOrLbs === 'kg' ? 'kg' : 'lb';
                // allProducts is only [product] because we want to calculate the rates for the product only when chargeable weigh
                const rates = getFieldRatesForProduct({ fieldBuyingRates, product, buyingRates, transportationMethod: 'air', allProducts: [product], organization });
                return {
                    fieldValueId: product?.id,
                    productsInChargeLine: [product],
                    title: getProductName({ product }),
                    description: product.dangerous ? dangerousText(product) : null,
                    quantityMultiplier: multiplier,
                    multiplierText: `${multiplierText} (gw)`,
                    multiplierTextSingular: `${multiplierText} (gw)`,
                    buyingRates: _.flatten(rates)
                }
            });
        },
    }, {
        label: 'Chargeable Weight',
        value: 'per-unit',
        isAggregated: true,
        getChargeLines: ({ products, buyingRates, fieldBuyingRates, organization }) => {
            const { airFreight } = splitProductsByType({ products, organization });
            return cleanupChargeLineRates(_.map(airFreight, (productsAndWeights, postfix) => {
                const productsInChargeLine = productsAndWeights.map(p => p.product);
                const rates = productsInChargeLine.map(p => getFieldRatesForProduct({ fieldBuyingRates, product: p, buyingRates, transportationMethod: 'air', allProducts: products, organization }));
                return {
                    fieldValueId: productsAndWeights[0]?.product?.id,
                    productsInChargeLine,
                    quantityMultiplier: _.sumBy(productsAndWeights, p => p.weight.val),
                    multiplierText: postfix + ' (cw)',
                    multiplierTextSingular: postfix + ' (cw)',
                    buyingRates: airJoinChargeableWeightRates({ rates: _.flatten(rates), postfix, organization, totalMultiplier: _.sumBy(productsAndWeights, p => p.weight.val) })
                }
            }));
        }
    }, {
        label: 'Gross Weight',
        value: 'per-unit-weight',
        isAggregated: true,
        getChargeLines: ({ products, buyingRates, fieldBuyingRates, organization }) => {
            // No usable product data (e.g. opportunity saved without product info) — no gross-weight
            // charge line to show. Returning [] here keeps the preview from crashing on products[0].
            if (!products || products.length === 0) return [];
            const postfix = products[0]?.kgOrLbs === 'kg' ? 'kg' : 'lb';
            const rates = products.map(p => getFieldRatesForProduct({ fieldBuyingRates, product: p, buyingRates, transportationMethod: 'air', allProducts: products, organization }));
            return cleanupChargeLineRates([{
                fieldValueId: products[0]?.id,
                productsInChargeLine: products,
                quantityMultiplier: _.sumBy(products, p => calcWeight({ product: p }).val),
                multiplierText: postfix + ' (gw)',
                multiplierTextSingular: postfix + ' (gw)',
                buyingRates: airJoinChargeableWeightRates({ rates: _.flatten(rates), postfix, organization })
            }]);
        }
    }, {
        label: 'Shipment',
        value: 'flat',
        getChargeLines: ({ products, buyingRates, fieldBuyingRates, organization }) => {
            const rates = products.map(p => getFieldRatesForProduct({ fieldBuyingRates, product: p, buyingRates, transportationMethod: 'air', allProducts: products, organization }));
            const shipmentRates = _.uniqBy(_.flatten(rates.map(r => r.filter(br => br.multiplierText === 'shipment'))), r => r.field.id);
            return cleanupChargeLineRates([{
                fieldValueId: 'flat',
                productsInChargeLine: products,
                quantityMultiplier: 1,
                multiplierText: 'shipment',
                multiplierTextSingular: 'shipment',
                buyingRates: [
                    ..._.flatten(rates.map((r) => r.filter(br => br.multiplierText !== 'shipment'))),
                    ...shipmentRates
                ]
            }]);
        }
    }, {
        label: 'Custom priced',
        value: 'custom',
        getChargeLines: ({ field, products, buyingRates, fieldBuyingRates, organization }) => {
            const rates = products.map(p => getFieldRatesForProduct({ fieldBuyingRates, product: p, buyingRates, transportationMethod: 'air', allProducts: products, organization }));
            const shipmentRates = _.uniqBy(_.flatten(rates.map(r => r.filter(br => br.multiplierText === 'shipment'))), r => r.field.id);
            return cleanupChargeLineRates([{
                fieldValueId: 'custom',
                productsInChargeLine: products,
                quantityMultiplier: field?.values?.custom?.multiplier || 1,
                multiplierText: pluralize(field?.values?.custom?.multiplierText || '', field?.values?.custom?.multiplier || 1),
                multiplierTextSingular: pluralize(field?.values?.custom?.multiplierText || '', 1),
                // Strip shipment-priced rates from every product (not just the
                // non-first), then append the deduped shipmentRates once.
                // Otherwise the first product retained its shipment rate AND we
                // re-appended it, double-counting the per-shipment buying cost.
                buyingRates: [
                    ..._.flatten(rates.map(r => r.filter(br => br.multiplierText !== 'shipment'))),
                    ...shipmentRates
                ]
            }]);
        }
    }, {
        label: 'Text',
        value: 'string'
    }],
    sea: [{
        label: 'Priced per unit',
        value: 'per-unit',
        isAggregated: true,
        getChargeLines: ({ products, buyingRates, fieldBuyingRates, organization }) => {
            const { containers, seaByWeightOrMeasurment } = splitProductsByType({ products, organization });
            const containerQuantites = _(containers).sumBy(containerQuantity);
            const containerRates = containers.map(p => getFieldRatesForProduct({ fieldBuyingRates, product: p, buyingRates, transportationMethod: 'sea', allProducts: products, organization }));
            // Drop the line when there are no container products at all, so a W/M-only
            // shipment does not show "x 0 containers". Containers that are there but count
            // zero keep their line and charge for nothing - an empty list of charge lines is
            // read as "the first one" all over the app, and that is undefined.
            return cleanupChargeLineRates(_.compact([ containers.length === 0 ? null : {
                fieldValueId: containers[0]?.id,
                productsInChargeLine: containers,
                quantityMultiplier: containerQuantites,
                multiplierText: pluralize('container', containerQuantites),
                multiplierTextSingular: 'container',
                buyingRates: _.flatten(containerRates)
            }, ..._.map(seaByWeightOrMeasurment, (productsAndWeights, postfix) => {
                const productsInChargeLine = productsAndWeights.map(p => p.product);
                const rates = productsInChargeLine.map(p => getFieldRatesForProduct({ fieldBuyingRates, product: p, buyingRates, transportationMethod: 'sea', allProducts: products, organization }));
                return {
                    fieldValueId: productsAndWeights[0]?.product?.id,
                    productsInChargeLine,
                    quantityMultiplier: _.sumBy(productsAndWeights, p => p.weight.val),
                    multiplierText: postfix,
                    multiplierTextSingular: postfix,
                    buyingRates: _.flatten(rates)
                }
            })]));
        }
    }, {
        label: 'Priced per type',
        value: 'per-unit-type',
        getChargeLines: ({ products, buyingRates, fieldBuyingRates, organization }) => {
            return products.map((product, i) => {
                const { multiplier, multiplierText } = getMultiplier({ product, transportationMethod: 'sea', allProducts: products, round: organization.airRoundChargeableWeight });
                const rates = getFieldRatesForProduct({ fieldBuyingRates, product, buyingRates, transportationMethod: 'sea', allProducts: [product], organization });
                return {
                    fieldValueId: product?.id,
                    productsInChargeLine: [product],
                    title: getProductName({ product }),
                    description: product.dangerous ? dangerousText(product) : null,
                    quantityMultiplier: multiplier,
                    multiplierText,
                    multiplierTextSingular: multiplierText,
                    buyingRates: rates,
                }
            });
        }
    }, {
        label: 'Shipment',
        value: 'flat',
        getChargeLines: ({ products, buyingRates, fieldBuyingRates, organization }) => {
            const rates = products.map(p => getFieldRatesForProduct({ fieldBuyingRates, product: p, buyingRates, transportationMethod: 'sea', allProducts: products, organization }));
            const allRates = _.uniqBy(_.flatten(rates), r => `${r.rate?.id}|${r.field?.id}`);
            return cleanupChargeLineRates([{
                fieldValueId: 'flat',
                productsInChargeLine: products,
                quantityMultiplier: 1,
                multiplierText: 'shipment',
                multiplierTextSingular: 'shipment',
                buyingRates: allRates
            }]);
        }
    }, {
        label: 'Custom priced',
        value: 'custom',
        getChargeLines: ({ field, products, buyingRates, fieldBuyingRates, organization }) => {
            const rates = products.map(p => getFieldRatesForProduct({ fieldBuyingRates, product: p, buyingRates, transportationMethod: 'sea', allProducts: products, organization }));
            const allRates = _.uniqBy(_.flatten(rates), r => `${r.rate?.id}|${r.field?.id}`);
            return cleanupChargeLineRates([{
                fieldValueId: 'custom',
                productsInChargeLine: products,
                quantityMultiplier: field?.values?.custom?.multiplier || 1,
                multiplierText: pluralize(field?.values?.custom?.multiplierText || '', field?.values?.custom?.multiplier || 1),
                multiplierTextSingular: pluralize(field?.values?.custom?.multiplierText || '', 1),
                buyingRates: allRates
            }]);
        }
    }, {
        label: 'Text',
        value: 'string'
    }],
    land: [{
        label: 'Priced per unit',
        value: 'per-unit',
        isAggregated: true,
        getChargeLines: ({ products, buyingRates, fieldBuyingRates, organization }) => {
            const { containers, landByWeightOrMeasurment, airFreight } = splitProductsByType({ products, transportationMethod: 'land', organization });
            // Belt and braces: the container split already keeps the land W/M types out,
            // 'By Dimensions' among them, so this filter should never have anything to do.
            const realContainers = containers.filter(c => c.type !== 'By Dimensions');
            const containerQuantites = _(realContainers).sumBy(containerQuantity);
            const containerRates = realContainers.map(p => getFieldRatesForProduct({ fieldBuyingRates, product: p, buyingRates, transportationMethod: 'land', allProducts: products, organization }));
            // Match the sea variant: omit the container charge line when there are no
            // actual containers, so W/M-only land shipments don't show "x 0 containers".
            // A container counting zero still keeps its line - see the sea note above.
            return cleanupChargeLineRates(_.compact([ realContainers.length === 0 ? null : {
                fieldValueId: realContainers[0]?.id,
                productsInChargeLine: realContainers,
                quantityMultiplier: containerQuantites,
                multiplierText: pluralize('container', containerQuantites),
                multiplierTextSingular: 'container',
                buyingRates: _.flatten(containerRates)
            }, ..._.map(landByWeightOrMeasurment, (productsAndWeights, postfix) => {
                const productsInChargeLine = productsAndWeights.map(p => p.product);
                const rates = productsInChargeLine.map(p => getFieldRatesForProduct({ fieldBuyingRates, product: p, buyingRates, transportationMethod: 'land', allProducts: products, organization }));
                return {
                    fieldValueId: productsAndWeights[0]?.product?.id,
                    productsInChargeLine,
                    quantityMultiplier: _.sumBy(productsAndWeights, p => p.weight.val),
                    multiplierText: postfix,
                    multiplierTextSingular: postfix,
                    buyingRates: _.flatten(rates)
                }
            }), ..._.map(airFreight, (productsAndWeights, postfix) => {
                // Air cargo on its trucking leg, priced by the same chargeable weight it goes by in
                // the air. splitProductsByType already sorts it into its own bucket and
                // calcChargeableWeight measures it on a land leg — reading the bucket here is what
                // was missing. Left out, an air product on a land opportunity fell between the
                // buckets, kept out of containers and out of the land W/M group, so it raised no
                // charge line at all and getFieldRatesForProduct found none to hang a supplier cost
                // on either. Handled exactly as the air variant above, down to the '(cw)' label.
                const productsInChargeLine = productsAndWeights.map(p => p.product);
                const rates = productsInChargeLine.map(p => getFieldRatesForProduct({ fieldBuyingRates, product: p, buyingRates, transportationMethod: 'land', allProducts: products, organization }));
                const totalMultiplier = _.sumBy(productsAndWeights, p => p.weight.val);
                return {
                    fieldValueId: productsAndWeights[0]?.product?.id,
                    productsInChargeLine,
                    quantityMultiplier: totalMultiplier,
                    multiplierText: postfix + ' (cw)',
                    multiplierTextSingular: postfix + ' (cw)',
                    buyingRates: airJoinChargeableWeightRates({ rates: _.flatten(rates), postfix, organization, totalMultiplier })
                }
            })]));
        }
    }, {
        label: 'Priced per type',
        value: 'per-unit-type',
        getChargeLines: ({ products, buyingRates, fieldBuyingRates, organization }) => {
            return products.map((product, i) => {
                const { multiplier, multiplierText } = getMultiplier({ product, transportationMethod: 'land', allProducts: products, round: organization.airRoundChargeableWeight });
                const rates = getFieldRatesForProduct({ fieldBuyingRates, product, buyingRates, transportationMethod: 'land', allProducts: products, organization });
                return {
                    fieldValueId: product?.id,
                    productsInChargeLine: [product],
                    title: getProductName({ product }),
                    description: product.dangerous ? dangerousText(product) : null,
                    quantityMultiplier: multiplier,
                    multiplierText,
                    multiplierTextSingular: multiplierText,
                    buyingRates: rates,
                }
            });
        }
    }, {
        label: 'Shipment',
        value: 'flat',
        getChargeLines: ({ products, buyingRates, fieldBuyingRates, organization }) => {
            const rates = products.map(p => getFieldRatesForProduct({ fieldBuyingRates, product: p, buyingRates, transportationMethod: 'land', allProducts: products, organization }));
            const shipmentRates = _.uniqBy(_.flatten(rates.map(r => r.filter(br => br.multiplierText === 'shipment'))), r => r.field.id);
            return cleanupChargeLineRates([{
                fieldValueId: 'flat',
                productsInChargeLine: products,
                quantityMultiplier: 1,
                multiplierText: 'shipment',
                multiplierTextSingular: 'shipment',
                buyingRates: [
                    ..._.flatten(rates.map((r) => r.filter(br => br.multiplierText !== 'shipment'))),
                    ...shipmentRates
                ]
            }]);
        }
    }, {
        label: 'Custom priced',
        value: 'custom',
        getChargeLines: ({ field, products, buyingRates, fieldBuyingRates, organization }) => {
            const rates = products.map(p => getFieldRatesForProduct({ fieldBuyingRates, product: p, buyingRates, transportationMethod: 'land', allProducts: products, organization }));
            const shipmentRates = _.uniqBy(_.flatten(rates.map(r => r.filter(br => br.multiplierText === 'shipment'))), r => r.field.id);
            return cleanupChargeLineRates([{
                fieldValueId: 'custom',
                productsInChargeLine: products,
                quantityMultiplier: field?.values?.custom?.multiplier || 1,
                multiplierText: pluralize(field?.values?.custom?.multiplierText || '', field?.values?.custom?.multiplier || 1),
                multiplierTextSingular: pluralize(field?.values?.custom?.multiplierText || '', 1),
                // Same fix as air: strip shipment from every per-product list,
                // then append the deduped shipmentRates once (avoids double-count).
                buyingRates: [
                    ..._.flatten(rates.map(r => r.filter(br => br.multiplierText !== 'shipment'))),
                    ...shipmentRates
                ]
            }]);
        }
    }, {
        label: 'Text',
        value: 'string'
    }],
};

// A missing quantity means one container; a quantity of zero means none. `quantity || 1`
// could not tell the two apart, so a product deliberately set to zero counted as one - which
// is how the uplift explainer, which prices the field at zero to find the part that does not
// scale, reported per-container costs as "per shipment".
function containerQuantity(product) {
    const quantity = parseInt(product.quantity);
    return _.isNaN(quantity) ? 1 : quantity;
}

function cleanupChargeLineRates(chargeLines) {
    // A cost priced per shipment belongs to the shipment rather than to each line, so it has to be
    // counted once however many lines a quote raises. Keeping it only on the first line assumed
    // that is where it lands — but the lines come out in a fixed order, containers then
    // weight-or-measurement then air, while the cost sits on whichever line carries its own
    // product. A per-shipment cost on anything but a container was therefore stripped from the one
    // line that held it and disappeared from the quote altogether, cost centre and all.
    //
    // Counted once still, on the first line that actually carries it.
    const counted = new Set();
    chargeLines.forEach(cl => {
        cl.buyingRates = (cl.buyingRates || []).filter(br => {
            if (br.multiplierText !== 'shipment') return true;
            const key = `${br.rateId || br.rate?.id}|${br.fieldId || br.field?.id}`;
            if (counted.has(key)) return false;
            counted.add(key);
            return true;
        });
    });

    return chargeLines;
}

function airJoinChargeableWeightRates({ rates, postfix, organization, totalMultiplier }) {
    const crRates = _.cloneDeep(rates).filter(r => r.multiplierText.includes('cw'));
    const otherRates = _.cloneDeep(rates).filter(r => !r.multiplierText.includes('cw'));

    const crRatesProducts = _.uniqBy(crRates.map(r => r.product), r => r.id);
    const { airFreight } = splitProductsByType({ products: crRatesProducts, organization });

    // Group by rate+field to consolidate duplicate entries (same rate field referenced by multiple products)
    const grouped = _(crRates).groupBy(r => `${r.rateId || r.rate?.id}|${r.fieldId || r.field?.id}`);
    const joinedRates = grouped.mapValues(v => v[0]).values().flatten().value();

    const totalCW = totalMultiplier || _.sumBy(airFreight[postfix], p => p.weight.val);

    // Check if we have per-product pricing: same supplier, same field title,
    // but different fieldIds (one field per product). In that case each rate
    // keeps its own per-product multiplier — sum of multipliers = totalCW.
    // Different field titles (FRT, THC, DOC) are different charges, not per-product.
    const rateGroups = _(joinedRates).groupBy(r => r.rateId || r.rate?.id).value();
    const hasPerProductFields = Object.values(rateGroups).some(group => {
        if (group.length <= 1) return false;
        const titles = group.map(r => r.field?.title);
        // All same title = per-product pricing for the same charge
        return titles.every(t => t === titles[0]);
    });

    if (!hasPerProductFields) {
        // Single rate, or consolidated rates: use totalCW
        joinedRates.forEach(r => {
            if (r.product?.type === 'Air freight') {
                // New unified Air freight: rewrite calculations[] to a single chargeable-weight entry
                r.product.calculations = [{ id: 'default', method: 'chargeable-weight', chargeableWeight: totalCW, weight: 0, quantity: 1 }];
                r.product.quantity = 1;
            } else {
                r.product.chargeableWeight = totalCW;
                r.product.quantity = 1;
                r.product.weight = 0;
                delete r.product.height;
                delete r.product.width;
                delete r.product.length;
                delete r.product.volume;
            }
            r.multiplier = totalCW;
        });
    }

    return [
        ...joinedRates,
        ...otherRates,
    ];
}
// The pricing types offered for a cost. Aggregated types are an air idea, so sea and land leave
// them out of the picker — but a cost can already be one, having arrived that way from a rate that
// prices per unit, and a Select whose value is not among its options renders blank. So whatever
// type a cost already has is always offered, whatever the mode. Without that the dropdown shows
// nothing selected on a perfectly good cost, and choosing either option silently repriced it.
export function buyingRateFieldTypes({ transportationMethod, organization, currentType }) {
    const all = FieldTypes[transportationMethod] || [];
    const offered = all.filter(ft =>
        ft.value !== 'custom'
        && ft.value !== 'string'
        && (transportationMethod === 'air' || !ft.isAggregated)
        && (!_.isFunction(ft.enabled) || ft.enabled({ organization, forBuyingRate: true }))
    );
    if (!currentType || offered.some(ft => ft.value === currentType)) return offered;
    const own = all.find(ft => ft.value === currentType);
    return own ? [...offered, own] : offered;
}
